import { useState } from "react";
import { Alert, Button, Modal } from "antd";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { getStripePromise } from "../../../../infrastructure/utils/stripeClient";
import { guides2CheckoutPath } from "../../../routes/routes";

// VIVA-1214 — formulario de pago de una guía (Stripe Payment Element).
//
// Recibe el clientSecret que devolvió create-payment-intent-generico y confirma
// el pago en el navegador. Lo que pase después —habilitar la guía— lo resuelve
// el webhook en servidor (CA-09); este componente solo informa el resultado.
//
// Mismo esquema que `purchaseHistory/utils/Stripe/PaymentModal.jsx` (cobro de
// reservas), con los textos del modulo de guias y sin crear el intento aqui:
// crearlo es trabajo del hook, para que la llave de idempotencia viva en un
// solo lugar.
const GuidePaymentForm = ({ t, email, amountLabel, onSucceeded, onProcessing, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorText("");

    try {
      // `redirect: "if_required"`: con tarjeta no se sale de la pagina. Si el
      // metodo exige redirigir (3DS de algunos bancos), Stripe regresa a la
      // misma liga de contratacion; la guia la habilita el webhook igual.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email || undefined,
          return_url: `${window.location.origin}/#${guides2CheckoutPath}`,
        },
        redirect: "if_required",
      });

      if (error) {
        // Stripe ya devuelve el motivo traducido al idioma del formulario.
        setErrorText(error.message || t("paymentConfirmError"));
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        onSucceeded(paymentIntent);
      } else if (paymentIntent?.status === "processing") {
        onProcessing(paymentIntent);
      } else {
        setErrorText(t("paymentConfirmError"));
      }
    } catch (error) {
      console.error("Error al confirmar el pago de la guía:", error);
      setErrorText(t("paymentConfirmError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {amountLabel ? <p className="g2-payment-amount">{t("paymentAmount", amountLabel)}</p> : null}
      <PaymentElement
        options={{ layout: "tabs" }}
        onReady={() => setReady(true)}
        onLoadError={() => setErrorText(t("paymentFormError"))}
      />
      {errorText ? (
        <Alert className="g2-payment-alert" type="error" showIcon message={errorText} />
      ) : null}
      <div className="g2-payment-actions">
        <Button onClick={onCancel} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={!stripe || !elements || !ready}
        >
          {t("payNow")}
        </Button>
      </div>
    </form>
  );
};

const GuidePaymentModal = ({
  open,
  clientSecret,
  lang,
  t,
  email,
  amountLabel,
  onSucceeded,
  onProcessing,
  onCancel,
}) => {
  const stripePromise = open ? getStripePromise() : null;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
      title={t("paymentTitle")}
      zIndex={3000}
    >
      {stripePromise && clientSecret ? (
        <Elements
          stripe={stripePromise}
          key={clientSecret}
          options={{
            clientSecret,
            locale: lang === "en" ? "en" : "es",
            appearance: { theme: "stripe" },
          }}
        >
          <GuidePaymentForm
            t={t}
            email={email}
            amountLabel={amountLabel}
            onSucceeded={onSucceeded}
            onProcessing={onProcessing}
            onCancel={onCancel}
          />
        </Elements>
      ) : null}
    </Modal>
  );
};

export default GuidePaymentModal;
